import React, { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallButtonProps {
  variant: 'header' | 'sidebar';
  language?: string;
  onInstallSuccess?: () => void;
}

export const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ 
  variant, 
  language = 'hi',
  onInstallSuccess 
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if running in standalone mode (actually installed and opened as app)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInWebAppiOS = (window.navigator as any).standalone === true;
    
    // Only hide if actually running as installed PWA
    if (isStandalone || isInWebAppiOS) {
      setIsInstalled(true);
      return;
    }
    
    // Clear any stale installed flags since we're in browser mode
    localStorage.removeItem('pwa_installed');

    // Check if dismissed before (only for 24 hours now, not 7 days)
    const wasDismissed = localStorage.getItem('pwa_install_dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed);
      // Show again after 24 hours (reduced from 7 days)
      if (Date.now() - dismissedTime < 24 * 60 * 60 * 1000) {
        setDismissed(true);
      } else {
        // Clear old dismissal
        localStorage.removeItem('pwa_install_dismissed');
      }
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Listen for install prompt - this means app is NOT installed
    const handleBeforeInstall = (e: Event) => {
      console.log('beforeinstallprompt fired!');
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstalled(false);  // Definitely not installed if we get this event
      // Clear any stale flags
      localStorage.removeItem('pwa_installed');
    };

    // Listen for successful install - NO tracking here, done in App.tsx
    const handleAppInstalled = () => {
      console.log('appinstalled fired!');
      setIsInstalled(true);
      setDeferredPrompt(null);
      if (onInstallSuccess) onInstallSuccess();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [onInstallSuccess]);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }

    if (!deferredPrompt) {
      // Show helpful message if no prompt available
      alert(language === 'hi' 
        ? 'Install करने के लिए:\n\n1. Chrome/Edge browser में खोलें\n2. Browser menu (⋮) पर click करें\n3. "Install App" या "Add to Home Screen" चुनें'
        : 'To install:\n\n1. Open in Chrome/Edge browser\n2. Click browser menu (⋮)\n3. Select "Install App" or "Add to Home Screen"'
      );
      return;
    }

    try {
      // Store prompt reference before nullifying
      const promptEvent = deferredPrompt;
      // Clear the deferred prompt immediately to prevent double-click
      setDeferredPrompt(null);
      
      // Show install prompt
      await promptEvent.prompt();
      
      // Wait for user choice (this happens in background)
      promptEvent.userChoice.then(({ outcome }) => {
        if (outcome === 'accepted') {
          setIsInstalled(true);
        }
      });
      
    } catch (error) {
      console.error('Install prompt error:', error);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  // Debug logging
  console.log('PWAInstallButton state:', { isInstalled, dismissed, deferredPrompt: !!deferredPrompt, isIOS });
  
  // TEMPORARILY: Show button always on preview for debugging
  const isPreview = window.location.hostname.includes('preview') || window.location.hostname === 'localhost';
  
  // Skip installed/dismissed check for preview mode - always show
  if (!isPreview) {
    if (isInstalled || dismissed) return null;
  }
  
  // Show button if:
  // 1. Preview mode (always show for testing)
  // 2. We have a deferred prompt (normal case)
  // 3. iOS device (show instructions)
  const shouldShow = isPreview || deferredPrompt || isIOS;
  
  if (!shouldShow) return null;

  // iOS Instructions Modal
  const IOSModal = () => (
    <div 
      className="fixed inset-0 bg-black/60 z-[100] flex items-end sm:items-center justify-center p-4"
      onClick={() => setShowIOSModal(false)}
    >
      <div 
        className="bg-white dark:bg-stone-800 rounded-3xl w-full max-w-md p-6 animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold dark:text-white">
            {language === 'hi' ? 'App Install करें' : 'Install App'}
          </h3>
          <button 
            onClick={() => setShowIOSModal(false)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-stone-700 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 text-gray-600 dark:text-stone-300">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <span className="text-blue-600 font-bold">1</span>
            </div>
            <p>
              {language === 'hi' 
                ? 'Safari browser में नीचे Share बटन पर tap करें' 
                : 'Tap the Share button at the bottom of Safari'}
              <Share className="inline ml-2 text-blue-500" size={18} />
            </p>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <span className="text-blue-600 font-bold">2</span>
            </div>
            <p>
              {language === 'hi' 
                ? '"Add to Home Screen" पर tap करें' 
                : 'Tap "Add to Home Screen"'}
              <Plus className="inline ml-2 text-blue-500" size={18} />
            </p>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <span className="text-blue-600 font-bold">3</span>
            </div>
            <p>
              {language === 'hi' 
                ? '"Add" पर tap करें - Done!' 
                : 'Tap "Add" - Done!'}
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowIOSModal(false)}
          className="mt-6 w-full py-3 bg-saffron text-white rounded-2xl font-bold"
        >
          {language === 'hi' ? 'समझ गया' : 'Got it'}
        </button>
      </div>
    </div>
  );

  // Header variant - compact icon with NEW badge
  if (variant === 'header') {
    return (
      <>
        <div className="relative">
          <span className="absolute -top-1.5 -right-1.5 px-1 py-0.5 text-[6px] font-black bg-red-500 text-white rounded-full z-10 leading-none animate-pulse">
            NEW
          </span>
          <button
            onClick={handleInstallClick}
            className="relative w-5 h-5 flex items-center justify-center rounded bg-gradient-to-r from-saffron to-orange-500 text-white shadow-md shadow-orange-500/30 hover:shadow-orange-500/50 transition-all active:scale-90"
            title={language === 'hi' ? 'App Install करें' : 'Install App'}
            data-testid="header-install-btn"
          >
            <Download size={11} />
          </button>
        </div>
        {showIOSModal && <IOSModal />}
      </>
    );
  }

  // Sidebar variant - full card with dismiss
  return (
    <>
      <div className="relative mx-3 mb-3">
        {/* NEW Badge */}
        <span className="absolute -top-2 left-4 px-2 py-0.5 text-[9px] font-black bg-red-500 text-white rounded-full animate-pulse z-10 shadow-md">
          NEW
        </span>
        
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute -top-1 -right-1 w-5 h-5 bg-gray-200 dark:bg-stone-600 rounded-full flex items-center justify-center hover:bg-gray-300 dark:hover:bg-stone-500 z-10"
          title="Dismiss"
        >
          <X size={12} />
        </button>

        <button
          onClick={handleInstallClick}
          data-testid="sidebar-install-btn"
          className="w-full p-4 rounded-2xl bg-gradient-to-r from-saffron to-orange-500 text-white shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all active:scale-[0.98] animate-subtle-pulse"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Download size={22} className="animate-bounce-slow" />
            </div>
            <div className="text-left">
              <div className="font-bold text-sm">
                {language === 'hi' ? 'App Install करें' : 'Install App'}
              </div>
              <div className="text-[10px] opacity-80">
                {language === 'hi' ? 'FREE • Offline • Fast' : 'FREE • Offline • Fast'}
              </div>
            </div>
          </div>
        </button>
      </div>

      {showIOSModal && <IOSModal />}
    </>
  );
};

export default PWAInstallButton;
